import { useMemo, useState, useEffect } from 'react';
import { useCountry } from './useCountry';
import { translations, getLanguageFromCountry } from '@/utils/translations';

// Simple namespace translations loader
const loadNamespaceTranslations = async (namespace: string, lang: string): Promise<Record<string, string>> => {
  try {
    const module = await import(`@/locales/${lang}/${namespace}.json`);
    return module.default || {};
  } catch (error) {
    // Fallback to English if language not found
    try {
      const module = await import(`@/locales/en/${namespace}.json`);
      return module.default || {};
    } catch (fallbackError) {
      console.warn(`Translation namespace '${namespace}' not found for '${lang}' or 'en'`);
      return {};
    }
  }
};

export const useTranslation = (namespace?: string) => {
  const { country } = useCountry();
  const language = useMemo(() => getLanguageFromCountry(country), [country]);
  const [namespaceTranslations, setNamespaceTranslations] = useState<Record<string, string>>({});
  
  useEffect(() => {
    if (namespace) {
      loadNamespaceTranslations(namespace, language).then(setNamespaceTranslations);
    }
  }, [namespace, language]);
  
  const t = useMemo(() => {
    return (key: string): string => {
      // If namespace is provided, check namespace translations first
      if (namespace && namespaceTranslations[key]) {
        return namespaceTranslations[key];
      }
      // Fall back to main translations
      return (translations[language] as Record<string, string>)[key] || (translations['ro'] as Record<string, string>)[key] || key;
    };
  }, [language, namespace, namespaceTranslations]);
  
  return { t, language };
};
